import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import qs from "query-string";
import React from "react";
import { connect } from "react-redux";
import * as router from "react-router-redux";

import * as wevoConfigurationConstants from "../modules/configure-wevo/constants";
import { deviceActions } from "../modules/devices";
import { setPageTitle } from "../modules/title";
import { trackerActions } from "../modules/tracker";
import { ConfigureProjectEvents } from "../modules/tracker/event-names";
import { wevoActions, wevoSelectors } from "../modules/wevos";

import PreLoader from "@wevo/react-components/lib/PreLoader";
import AddAudience from "../components/CreateTest/AddAudience";
import CreateTestSideNav from "../components/CreateTest/CreateTestSideNav";
import ReviewTest from "../components/CreateTest/ReviewTest";
import PickCTAs from "../components/CreateTest/PickCTAs";
import ProjectDefinition from "../components/CreateTest/ProjectDefinition";
import SelectPages from "../components/CreateTest/SelectPages";
import styles from "../styles/createTest/WevoConfigurationContainer.css";
import { DEVICES } from "../modules/devices/constants";

class WevoConfigurationContainer extends React.PureComponent {
    static CREATE_TEST_TEXT = "Untitled New Test";
    static CONFIGURATION_STEPS = [
        {
            targetComponent: ProjectDefinition,
            wevoConfigurationKey:
                wevoConfigurationConstants.PROJECT_DEFINITION_REDUCER_KEY,
            labels: {
                leftBar: "Define Test"
            },
            hasSideNav: true,
            canEdit: () => true,
            isValid: wevo =>
                !isNil(wevo) && !isNil(wevo.id) && !isEmpty(wevo.devices)
        },
        {
            targetComponent: AddAudience,
            wevoConfigurationKey:
                wevoConfigurationConstants.SELECT_AUDIENCE_REDUCER_KEY,
            labels: {
                leftBar: "Add Audience",
                title: "Audience",
                subtitle: `Select the target audience for this test.`
            },
            hasSideNav: true,
            canEdit: wevo => !isNil(wevo),
            isValid: wevo =>
                !isNil(wevo) &&
                !isNil(wevo.audiences) &&
                wevo.audiences.every(AddAudience.isValidAudience)
        },
        {
            targetComponent: SelectPages,
            wevoConfigurationKey:
                wevoConfigurationConstants.SELECT_PAGES_REDUCER_KEY,
            labels: {
                leftBar: "Select Pages",
                title: "Select Pages",
                subtitle: "Drag and drop pages to upload or browse"
            },
            hasSideNav: true,
            canEdit: wevo => !isNil(wevo),
            isValid: wevo =>
                !isNil(wevo) &&
                !isNil(wevo.pages) &&
                wevo.pages.filter(p => p.isActive).length >= 1
        },
        {
            targetComponent: PickCTAs,
            wevoConfigurationKey:
                wevoConfigurationConstants.SELECT_CTAS_REDUCER_KEY,
            labels: {
                leftBar: "Pick CTAs",
                title: "Pick Calls to Action (CTAs)",
                subtitle: `Select the most important button or CTAs that drive conversion on your pages.`
            },
            hasSideNav: true,
            canEdit: wevo => {
                return (
                    !isNil(wevo) &&
                    !isNil(wevo.pages) &&
                    !isEmpty(wevo.pages) &&
                    wevo.pages.reduce((count, page) => {
                        if (page.isActive) {
                            count++;
                        }
                        return count;
                    }, 0) >= 1
                );
            },
            isValid: wevo => {
                if (isNil(wevo)) {
                    return false;
                }
                const { pages } = wevo;
                if (isNil(pages) || isEmpty(pages)) {
                    return false;
                }
                const activePages = pages.filter(page => {
                    return page && page.isActive;
                });
                if (activePages.length === 0) {
                    return false;
                }
                return activePages.every(page => {
                    if (!page.ctas || page.ctas.length === 0) {
                        return false;
                    }
                    const activeCtas = page.ctas.reduce((count, cta) => {
                        if (cta && cta.isActive) {
                            count++;
                        }
                        return count;
                    }, 0);
                    return activeCtas > 0;
                });
            }
        },
        {
            targetComponent: ReviewTest,
            wevoConfigurationKey:
                wevoConfigurationConstants.LAUNCH_PROJECT_REDUCER_KEY,
            labels: {
                leftBar: "Review"
            },
            hasSideNav: false,
            canEdit: wevo => !isNil(wevo),
            isValid: wevo => {
                return !isNil(wevo) && !isNil(wevo.dateSubmitted);
            }
        }
    ];

    constructor(props) {
        super(props);
        this.changeStep = this.changeStep.bind(this);
        this.registerNextClick = this.registerNextClick.bind(this);
        this.setActiveStep = this.setActiveStep.bind(this);
        this.saveAndExit = this.saveAndExit.bind(this);
    }

    componentWillMount() {
        const { wevo, wevoId, dispatch } = this.props;

        if (!isNil(wevoId) && !(wevo && wevo.pages && wevo.audiences)) {
            dispatch(wevoActions.fetchWevo(wevoId));
        }

        dispatch(deviceActions.fetchDevices());
        this.props.trackEvent(
            wevoId
                ? ConfigureProjectEvents.DID_NAVIGATE_TO_EDIT_PROJECT
                : ConfigureProjectEvents.DID_NAVIGATE_TO_CREATE_PROJECT,
            {
                wevo_id: wevoId
            }
        );
        this.setActiveStep(this.props);
        /* Determine Page Title */
    }

    componentWillReceiveProps(nextProps) {
        const currentPageTitle = nextProps.title.text;
        if (isNil(nextProps.wevoId)) {
            if (
                currentPageTitle !== WevoConfigurationContainer.CREATE_TEST_TEXT
            ) {
                nextProps.updateHeaderTitle(
                    WevoConfigurationContainer.CREATE_TEST_TEXT
                );
            }
        } else {
            const name = nextProps.wevo && nextProps.wevo.name;
            const device = nextProps.wevo && nextProps.wevo.devices[0];
            if (name && name !== currentPageTitle) {
                nextProps.updateHeaderTitle(name, DEVICES[device.id].icon);
            }
        }
        this.setActiveStep(nextProps);
    }

    setActiveStep(props) {
        const configSteps = WevoConfigurationContainer.CONFIGURATION_STEPS;
        const parsedStep = props.step;
        let activeStepIndex;
        if (
            !isNil(parsedStep) &&
            parsedStep >= 0 &&
            parsedStep < configSteps.length &&
            configSteps[parsedStep].canEdit(props.wevo)
        ) {
            activeStepIndex = parsedStep;
        } else if (isNil(props.wevoId)) {
            activeStepIndex = 0;
        } else {
            activeStepIndex = configSteps.length - 1;
        }
        this.setState({
            activeStepIndex
        });
    }

    changeStep(index, wevoId) {
        const { navigateTo, location, teamId } = this.props;
        let { pathname } = location;
        const queryString = qs.stringify({ step: index + 1 });
        if (!isNil(wevoId)) {
            pathname = `/team/${teamId}/edit/${wevoId}`;
        }
        navigateTo(
            `${pathname}${!isEmpty(queryString) ? `?${queryString}` : ""}`
        );
    }

    registerNextClick(nextItem, wevoId) {
        const { target } = nextItem;
        if (!isNil(target)) {
            const steps = WevoConfigurationContainer.CONFIGURATION_STEPS;
            const { activeStepIndex } = this.state;
            const activeStep = steps[activeStepIndex];
            const nextStep = steps[target];
            this.props.trackEvent(
                ConfigureProjectEvents.DID_CHANGE_NAVIGATION_STEP_IN_CONTENT,
                {
                    from_step: activeStep.labels.leftBar,
                    to_step: nextStep.labels.leftBar
                }
            );
            this.changeStep(target, wevoId);
        }
    }

    saveAndExit() {
        this.props.trackEvent(
            ConfigureProjectEvents.DID_CLICK_REVIEW_SAVE_AND_EXIT
        );
        this.props.navigateTo(`/team/${this.props.wevo.ownerTeamId}/dashboard`);
    }

    render() {
        const { wevo, devicesLoaded, wevoId, entities } = this.props;
        if (!devicesLoaded) {
            return <PreLoader />;
        }
        if (!isNil(wevoId) && !(wevo && wevo.pages && wevo.audiences)) {
            return <PreLoader />;
        }

        const { activeStepIndex } = this.state;
        const steps = WevoConfigurationContainer.CONFIGURATION_STEPS;
        const activeStep = steps[activeStepIndex];
        let nextStep;
        if (activeStepIndex === steps.length - 1) {
            nextStep = {
                text: "Launch Test",
                target: null
            };
        } else {
            const nextStepObject = steps[activeStepIndex + 1];
            nextStep = {
                text:
                    nextStepObject.labels.nextButton ||
                    nextStepObject.labels.leftBar,
                target: activeStepIndex + 1,
                canEdit: nextStepObject.canEdit(wevo)
            };
        }

        const navSteps = steps.map(step => {
            return {
                label: step.labels.leftBar,
                isEnabled: step.canEdit(wevo),
                isComplete: step.isValid(wevo)
            };
        });

        const CreateTestElement = activeStep.targetComponent;

        if (activeStep.hasSideNav) {
            return (
                <div>
                    <div className={styles.leftNav}>
                        <CreateTestSideNav
                            currentStep={activeStepIndex}
                            steps={navSteps}
                            onSaveAndExit={this.saveAndExit}
                            onStepClick={index =>
                                this.changeStep(index, this.props.wevoId)
                            }
                        />
                    </div>

                    <div className={styles.contentWithLeftNav}>
                        <div className={styles.contentContainer}>
                            <CreateTestElement
                                nextStepText={nextStep.text}
                                toNextStep={this.registerNextClick.bind(
                                    this,
                                    nextStep
                                )}
                                target={activeStep.targetComponent}
                                entities={entities}
                                devicesLoaded={this.props.devicesLoaded}
                                teamId={this.props.teamId}
                                location={this.props.location}
                                wevo={wevo}
                                wevoId={this.props.wevoId}
                                loadingStateKey={
                                    activeStep.wevoConfigurationKey
                                }
                                loadingState={
                                    this.props.configureWevo[
                                        activeStep.wevoConfigurationKey
                                    ]
                                }
                                dispatch={this.props.dispatch}
                                steps={steps}
                                stepLabels={activeStep.labels}
                                launchWevo={this.props.launchWevo}
                                navigateTo={this.props.navigateTo}
                                isReadOnly={
                                    !isNil(wevo) && wevo.status !== "draft"
                                }
                                trackEvent={this.props.trackEvent}
                            />
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <div className={styles.contentContainer}>
                    <CreateTestElement
                        nextStepText={nextStep.text}
                        toNextStep={this.registerNextClick.bind(this, nextStep)}
                        target={activeStep.targetComponent}
                        entities={entities}
                        devicesLoaded={this.props.devicesLoaded}
                        teamId={this.props.teamId}
                        location={this.props.location}
                        wevo={wevo}
                        wevoId={this.props.wevoId}
                        loadingStateKey={activeStep.wevoConfigurationKey}
                        loadingState={
                            this.props.configureWevo[
                                activeStep.wevoConfigurationKey
                            ]
                        }
                        dispatch={this.props.dispatch}
                        steps={steps}
                        stepLabels={activeStep.labels}
                        launchWevo={this.props.launchWevo}
                        navigateTo={this.props.navigateTo}
                        isReadOnly={!isNil(wevo) && wevo.status !== "draft"}
                        trackEvent={this.props.trackEvent}
                    />
                </div>
            );
        }
    }
}

function mapStateToProps(state, props) {
    const { entities, session, devices, configureWevo, queryString } = state;
    const { userId, teamId } = session;

    const paramWevoId = props.match.params.wevoId;
    const wevoId = isNil(paramWevoId)
        ? null
        : parseInt(props.match.params.wevoId, 10);
    const deviceLoadingStatus = devices.fetchStatus;

    // step is 1-indexed in the query string but arrays are 0-indexed so we need to subtract 1
    const step = parseInt(queryString.step, 10) - 1;
    return {
        title: state.title,
        entities,
        devices,
        configureWevo,
        userId,
        teamId,
        wevoId,
        devicesLoaded: deviceLoadingStatus === "loaded",
        step,
        wevo: wevoSelectors.getWevoDetailById(entities, wevoId)
    };
}

function mapDispatchToProps(dispatch) {
    return {
        dispatch,
        updateHeaderTitle: (name, icon) => dispatch(setPageTitle(name, icon)),
        fetchWevoDetail: wevoId =>
            dispatch(wevoActions.fetchWevoIfNeeded(wevoId)),
        navigateTo: path => dispatch(router.push(path)),
        launchWevo: (wevo, onSuccessUrl) =>
            dispatch(wevoActions.launchWevo(wevo, onSuccessUrl)),
        trackEvent: (eventName, eventProperties) =>
            dispatch(trackerActions.trackEvent(eventName, eventProperties))
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(WevoConfigurationContainer);



// WEBPACK FOOTER //
// ./src/containers/WevoConfigurationContainer.js